import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck, Clock, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function nextDays(count) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    out.push(day);
  }
  return out;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name"),
  email: z.string().trim().min(1, "Email is required").email("Please enter a valid email address."),
  company: z.string().optional(),
  phone: z.string().optional(),
  topic: z.string().optional(),
  message: z.string().optional(),
  company_url: z.string().optional(),
});

const TOPICS = ["AI & Machine Learning", "Data Engineering", "Analytics & BI", "Predictive Analytics", "Cloud Data", "Data Automation", "Not sure yet"];
const fieldCls = "w-full rounded-xl border border-[#111827]/12 bg-white px-4 py-3 text-sm text-[#111827] placeholder:text-[#9CA3AF] outline-none transition-colors focus:border-[#1677FF]";

export default function Consultation() {
  const days = useMemo(() => nextDays(14), []);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);
  const firstOpen = days.find((d, idx) => idx > 0 && d.getDay() !== 0 && d.getDay() !== 6) || days[0];
  const [selectedDate, setSelectedDate] = useState(iso(firstOpen));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const { register, handleSubmit, reset, getValues, setFocus, trigger, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const updateMobile = () => setIsMobile(mediaQuery.matches);
    updateMobile();
    mediaQuery.addEventListener("change", updateMobile);
    return () => mediaQuery.removeEventListener("change", updateMobile);
  }, []);

  useEffect(() => {
    let active = true;
    setLoadingSlots(true);
    setSlot(null);
    setSlots([]); // Clear old slots immediately when date changes
    axios.get(`${API}/consultation/availability`, { params: { date: selectedDate } })
      .then((r) => { if (active) setSlots(r.data.slots || []); })
      .catch(() => { if (active) setSlots([]); })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [selectedDate]);

  const selectSlot = async (time) => {
    const { name, email } = getValues();
    const nameMissing = !name?.trim();
    const emailMissing = !email?.trim();

    if (nameMissing || emailMissing) {
      await trigger(["name", "email"]);
      toast.error("Please enter your name and email before selecting a consultation time.");
      setFocus(nameMissing ? "name" : "email");
      return;
    }

    const emailValid = z.string().email().safeParse(email.trim()).success;
    if (!emailValid) {
      await trigger("email");
      toast.error("Please enter a valid email address.");
      setFocus("email");
      return;
    }

    const nameValid = await trigger("name");
    if (!nameValid) {
      setFocus("name");
      return;
    }

    setSlot(time);
  };

  const onSubmit = async (data) => {
    if (!slot) { toast.error("Please pick a time slot."); return; }
    const confirmationEmail = data.email;
    try {
      await axios.post(`${API}/consultation`, { ...data, date: selectedDate, time: slot });
      setConfirmed({ date: selectedDate, time: slot, email: confirmationEmail });
      toast.success("Consultation booked — check your email for confirmation.");
      reset();
      setSlot(null);
      // refresh slots so booked one shows unavailable
      const r = await axios.get(`${API}/consultation/availability`, { params: { date: selectedDate } });
      setSlots(r.data.slots || []);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("This consultation slot was just booked by someone else. Please select another available time.");
        setSlot(null);
        try {
          const r = await axios.get(`${API}/consultation/availability`, { params: { date: selectedDate } });
          setSlots(r.data.slots || []);
        } catch {
          setSlots([]);
        }
        return;
      }
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };

  const continueToTime = async () => {
    const valid = await trigger(["name", "email"]);
    if (!valid) {
      const values = getValues();
      setFocus(!values.name?.trim() ? "name" : "email");
      return;
    }
    setMobileStep(2);
  };

  const prettyConfirmed = confirmed
    ? (() => { const d = new Date(confirmed.date + "T00:00:00"); return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} at ${confirmed.time} IST`; })()
    : "";

  return (
    <>
      <SEO
        title="Book a Consultation | All Data Tech"
        description="Book a consultation with All Data Tech. Pick a time that works and we'll help you scope the right data, AI or analytics approach."
        path="/consultation"
      />
      <PageHero
        eyebrow="Book a Consultation"
        title={<>Pick a time that <span className="text-gradient">works for you.</span></>}
        sub="Choose a slot and share a little context. We'll confirm by email and come prepared to talk about your goals. Availability shown Mon–Fri (IST)."
      />

      <section className="ad-section bg-white pt-6">
        <div className="ad-container">
          <AnimatePresence mode="wait">
            {confirmed ? (
              <motion.div key="confirmed" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-xl rounded-2xl border border-[#1677FF]/20 bg-[#1677FF]/5 p-10 text-center" data-testid="booking-confirmed">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1677FF] text-white"><CheckCircle2 className="h-7 w-7" /></span>
                <h2 className="mt-6 text-2xl font-extrabold text-[#111827]">You're booked in.</h2>
                <p className="mt-3 text-[#4B5563]">Your consultation is scheduled for</p>
                <p className="mt-1 text-lg font-bold text-[#111827]">{prettyConfirmed}</p>
                <p className="mt-4 text-sm text-[#6B7280]">Confirmation email sent to <span className="font-semibold text-[#374151] break-all">{confirmed.email}</span>. We look forward to speaking with you.</p>
                <button onClick={() => { setConfirmed(null); setMobileStep(1); }} data-testid="book-another" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] hover:text-[#00C7D9]">
                  <ArrowLeft className="h-4 w-4" /> Book another time
                </button>
              </motion.div>
            ) : (
              <motion.div key="scheduler" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid min-w-0 gap-8 lg:grid-cols-2">
                <div className="order-0 mb-1 flex items-center justify-between text-xs font-semibold text-[#6B7280] lg:hidden" aria-label="Booking progress">
                  <span className={mobileStep === 1 ? "text-[#1677FF]" : ""}>1. Your details</span>
                  <span aria-hidden="true">→</span>
                  <span className={mobileStep === 2 ? "text-[#1677FF]" : ""}>2. Choose time</span>
                  <span aria-hidden="true">→</span>
                  <span>3. Confirmation</span>
                </div>
                {/* Date + slots */}
                <Reveal className={`order-2 min-w-0 lg:order-none ${isMobile && mobileStep !== 2 ? "hidden" : ""}`}>
                  <button type="button" onClick={() => setMobileStep(1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] hover:text-[#00C7D9] lg:hidden">
                    <ArrowLeft className="h-4 w-4" /> Back to your details
                  </button>
                  <div className="rounded-2xl border border-[#111827]/8 bg-[#F7F8FA] p-6 lg:p-7">
                    <div className="flex items-center gap-2 text-sm font-bold text-[#111827]">
                      <CalendarCheck className="h-5 w-5 text-[#1677FF]" /> Select a date
                    </div>
                    <div className="mt-4 -mx-1 flex min-w-0 gap-2 overflow-x-auto overscroll-x-contain px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="date-strip">
                      {days.map((d) => {
                        const key = iso(d);
                        const weekend = d.getDay() === 0 || d.getDay() === 6;
                        const active = key === selectedDate;
                        return (
                          <button
                            key={key}
                            disabled={weekend}
                            onClick={() => setSelectedDate(key)}
                            data-testid={`date-${key}`}
                            className={`flex min-w-[64px] flex-shrink-0 flex-col items-center rounded-xl border px-3 py-2.5 transition-all ${
                              weekend ? "cursor-not-allowed border-[#111827]/6 bg-white/50 text-[#C4CAD3]"
                              : active ? "border-[#1677FF] bg-[#1677FF] text-white"
                              : "border-[#111827]/10 bg-white text-[#374151] hover:border-[#1677FF]/50"
                            }`}
                          >
                            <span className="text-[11px] font-semibold uppercase">{WEEKDAYS[d.getDay()]}</span>
                            <span className="text-lg font-extrabold leading-tight">{d.getDate()}</span>
                            <span className="text-[10px]">{MONTHS[d.getMonth()]}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-6 flex items-center gap-2 text-sm font-bold text-[#111827]">
                      <Clock className="h-5 w-5 text-[#1677FF]" /> Available times <span className="font-normal text-[#6B7280]">(IST)</span>
                    </div>
                    <div className="mt-4 min-h-[120px]" data-testid="slot-grid">
                      {loadingSlots ? (
                        <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="h-4 w-4 animate-spin" /> Loading slots…</div>
                      ) : slots.some((s) => s.available) ? (
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {slots.map((s) => (
                            <button
                              key={s.time}
                              disabled={!s.available}
                              onClick={() => selectSlot(s.time)}
                              data-testid={`slot-${s.time}`}
                              className={`rounded-lg border px-2 py-2.5 text-sm font-semibold transition-all ${
                                !s.available ? "cursor-not-allowed border-[#111827]/6 bg-[#F3F5F7] text-[#C4CAD3] line-through"
                                : slot === s.time ? "border-[#1677FF] bg-[#1677FF] text-white"
                                : "border-[#111827]/10 bg-white text-[#374151] hover:border-[#1677FF]/60"
                              }`}
                            >
                              {s.time}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[#6B7280]">No available times on this date. Please choose another day.</p>
                      )}
                    </div>
                  </div>
                </Reveal>

                {/* Details form */}
                <Reveal index={1} className="order-1 min-w-0 lg:order-none">
                  <form onSubmit={handleSubmit(onSubmit)} data-testid="booking-form" className="rounded-2xl border border-[#111827]/8 bg-white p-6 lg:p-7" noValidate>
                    <h2 className="text-lg font-bold text-[#111827]">Your details</h2>
                    <p className="mt-1 text-sm text-[#6B7280]">
                      {slot ? <>Selected: <span className="font-semibold text-[#1677FF]">{slot} IST</span></> : "Pick a time slot on the left to continue."}
                    </p>

                    <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" {...register("company_url")} className="absolute left-[-9999px] h-0 w-0 opacity-0" />

                    <div className="mt-5 space-y-4">
                      <Field label="Name" error={errors.name}>
                        <input {...register("name")} data-testid="booking-name" className={fieldCls} placeholder="Enter your name" />
                      </Field>
                      <Field label="Work Email" error={errors.email}>
                        <input {...register("email")} type="email" data-testid="booking-email" className={fieldCls} placeholder="you@company.com" />
                      </Field>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Company"><input {...register("company")} data-testid="booking-company" className={fieldCls} placeholder="Company" /></Field>
                        <Field label="Phone"><input {...register("phone")} data-testid="booking-phone" className={fieldCls} placeholder="+1 555 000 0000" /></Field>
                      </div>
                      <Field label="Topic">
                        <select {...register("topic")} data-testid="booking-topic" className={fieldCls} defaultValue="">
                          <option value="" disabled>Select a topic</option>
                          {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Anything to share? (optional)">
                        <textarea {...register("message")} data-testid="booking-message" rows={3} className={fieldCls} placeholder="A sentence or two about what you'd like to discuss." />
                      </Field>
                    </div>

                    {isMobile && mobileStep === 1 ? (
                      <button type="button" onClick={continueToTime} data-testid="booking-continue"
                        className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#1677FF] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#0f5fe0] hover:shadow-[0_12px_30px_-10px_rgba(22,119,255,0.6)]">
                        Continue
                      </button>
                    ) : slot && (
                      <button type="submit" disabled={isSubmitting} data-testid="booking-submit"
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1677FF] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#0f5fe0] hover:shadow-[0_12px_30px_-10px_rgba(22,119,255,0.6)] disabled:cursor-not-allowed disabled:opacity-60">
                        {isSubmitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Booking…</>) : "Confirm Booking"}
                      </button>
                    )}
                  </form>
                </Reveal>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[#374151]">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-xs text-red-500" data-testid="field-error">{error.message}</span>}
    </label>
  );
}
